import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Mutation } from 'react-apollo';
import { Col, Row } from 'reactstrap';
import * as yup from 'yup';
import { displayError, getFullName, logAmplitudeEvent } from '../utilities';
import ApptBookingLongForm from './ApptBookingLongForm';
import PatientSearchByPhone from './PatientSearchByPhone';
// import { toast } from 'react-toastify';
import { showConfirmation } from '../ConfirmationDialogService';
import { CREATE_BOOKING } from '../queries';
import BookingInfo from './BookingInfo';
import Veil from './Veil';

const styles = {
  docName: {
    fontWeight: 'bold'
  },
  docSpl: {
    color: '#808080',
    textTransform: 'capitalize'
  },
  stDate: {
    fontWeight: 'bold'
  },
  topMargin: {
    marginTop: 10
  },
  slotHeader: {
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
    backgroundColor: '#b3b3b3',
    border: '1px solid #fff'
  },
  dayContainer: {
    border: '1px solid #c7c7c7',
    textAlign: 'center'
  },
  slotContainer: {
    borderTop: '1px solid #c7c7c7'
  },
  formLabel: {
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'flex-end'
  },
  confirmationContainer: {
    fontSize: '14px',
    color: '#000'
  },
  confirmationLabel: {
    fontWeight: 'bold'
  },
  confirmationValue: {
    fontWeight: 500,
    color: '#333'
  },
  confirmationQuestion: {
    fontSize: '18px',
    color: '#999'
  }
};

//o = data coming from userDB, map it to booking object
const getMappedJSON = o => ({
  mpid: o.mpid,
  userName: getFullName(o.first_name, o.last_name),
  userCountry: o.nationality_country,
  userMobile: o.mobile,
  userEmail: o.email
});

const schema = yup.object().shape({
  mpid: yup.string().nullable(),
  slotId: yup.string().required(),
  procedureName: yup.string('Procedure name is not provided').required(),
  userName: yup
    .string('Patient name should be alphabetical')
    .required('Patient name is required.'),
  userMobile: yup.string().required('Mobile number is required'),
  userCountry: yup.string().required('Select a country.'),
  userEmail: yup
    .string()
    .trim()
    .nullable()
    .email('Email is not well formatted'),
  guardianName: yup.string('Guardian name should be alphabetical'),
  guardianRel: yup.string('Please select relationship with guardian'),
  userGender: yup.string('Gender is required'),
  userDoB: yup.date('Please specify date of birth.')
});

export default class BookApptForm extends Component {
  state = {
    booking: {
      mpid: null,
      slotId: null,
      isPaid: false,
      userName: null,
      userMobile: null,
      userCountry: null,
      userEmail: null
    }
  };

  // showError = message => toast.error(`👎 ${message}`);
  submitBooking = async createBooking => {
    const { booking } = this.state;
    const { selectedSlot, procedureName } = this.props;
    booking.slotId = selectedSlot ? selectedSlot.id : null;
    booking.procedureName = procedureName;
    booking.userCountry = booking.userCountry || 'India';

    try {
      const myErrors = [];
      const formdata = await schema.validate(booking);

      // TODO: re-enable them later, Saurabh does not want these fields as mandatory
      /*
      if (!booking.mpid) {
        if (
          !booking.userGender ||
          (booking.userGender !== 'Male' && booking.userGender !== 'Female')
        ) {
          myErrors.push('Gender is required');
        }

        if (!booking.userDoB) {
          myErrors.push('Date of birth is required');
        }

        if (booking.userDoB) {
          formdata.userDoB = booking.userDoB.format('YYYY-MM-DD');
        }

        if (moment().diff(booking.userDoB, 'years') < 18) {
          if (!booking.guardianName) {
            myErrors.push('Guardian name is required for minors');
          }
          if (!booking.guardianRel) {
            myErrors.push('Guardian relationship is required for minors');
          }
        }
      }
      */
      if (booking.procedureName === 'Video Consultation') {
        throw new Error('Video consultation can not be booked through AMS.');
      }

      if (myErrors.length > 0) {
        const e = new Error();
        e.errors = myErrors;
        throw e;
      }

      // clean data
      if (booking.userDoB) {
        formdata.userDoB = booking.userDoB.format('YYYY-MM-DD');
      }

      await createBooking({
        variables: { createBooking: formdata },
        refetchQueries: [
          'SEARCH_BOOKINGS',
          'SLOTS_QUERY',
          'GET_BOOKINGS_STATUS'
        ]
      });
    } catch (e) {
      displayError(e);
    }
  };

  renderConfirmationField = (label, value) => {
    if (!value) return null;

    return (
      <p className="mb-1">
        <b style={styles.confirmationLabel}>{label}:</b>{' '}
        <span style={styles.confirmationValue}>{value}</span>
      </p>
    );
  };

  getConfirmationMessage = () => {
    const {
      selectedSlot,
      procedureName,
      doctorName,
      hospitalName,
      isScanBooking,
      scanName
    } = this.props;
    const { booking } = this.state;

    const NOT_AVAILABLE = 'N/A';
    const slotTime = selectedSlot
      ? moment(selectedSlot.startTime).format('DD-MMM hh:mm A')
      : '';
    const serviceType = isScanBooking ? 'Scan' : 'Procedure';
    const serviceName = isScanBooking ? scanName : procedureName;

    return (
      <div className="text-left mt-3" style={styles.confirmationContainer}>
        {this.renderConfirmationField(serviceType, serviceName)}
        {this.renderConfirmationField('Doctor', doctorName || NOT_AVAILABLE)}
        {this.renderConfirmationField('Slot', slotTime)}
        {this.renderConfirmationField(
          'Hospital',
          hospitalName || NOT_AVAILABLE
        )}
        {this.renderConfirmationField('Customer Name', booking.userName)}
        {this.renderConfirmationField('MPID', booking.mpid)}
        <p className="text-center mt-4" style={styles.confirmationQuestion}>
          Are you sure you want to book this appointment?
        </p>
      </div>
    );
  };

  handleUserData = selectedPatient => {
    //The next line is when user presses backspace on
    //empty input, this makes react-select to trigger onChange
    //as you are deleting empty string
    if (!selectedPatient) {
      this.setState({ isPatientSelected: false });
      return;
    }

    const { isNew, mobile, mpid } = selectedPatient;

    //clean up booking when user is reselected
    //to avoid data seeping into from prev user
    const booking = {};

    if (isNew) {
      const updatedBooking = { userMobile: mobile };
      this.setState({
        isNewPatient: true,
        isPatientSelected: true,
        booking: updatedBooking,
        hasMPI: !!mpid
      });
    } else {
      const userData = getMappedJSON(selectedPatient);
      const updatedBooking = Object.assign({}, booking, userData);
      this.setState({
        isNewPatient: false,
        isPatientSelected: true,
        booking: updatedBooking,
        hasMPI: !!mpid
      });
    }
  };

  updateBooking = (key, value) => {
    // console.log('key: ' + key + ', v: ' + value);
    const { booking } = this.state;
    booking[key] = value;
    this.setState({ booking });
  };

  //If the user is typing let's assume no user is selected
  handlePhoneUpdate = () => this.setState({ isPatientSelected: false });

  render() {
    const {
      selectedSlot,
      mode,
      hospitalId,
      procedureName,
      doctorId,
      isScanBooking
    } = this.props;
    const slotTime = selectedSlot
      ? moment(selectedSlot.startTime).format('ddd, DD MMM hh:mm a')
      : '';
    // console.log('slot sel', selectedSlot);
    const { isPatientSelected, booking, isNewPatient, hasMPI } = this.state;

    return (
      <Mutation
        mutation={CREATE_BOOKING}
        refetchQueries={() => ['GET_BOOKINGS_STATUS']}
        awaitRefetchQueries
        onCompleted={data => {
          const {
            createBooking: {
              objectName = '',
              procedureName = '',
              slotTime = '',
              userName = ''
            } = {}
          } = data;
          const eventProps = {
            procedureName,
            PatientName: userName,
            TimeOfAppointment: moment(slotTime).format('hh:mm A'),
            DateOfBooking: moment(slotTime).format('DD-MM-YYYY'),
            ConsultantName: objectName
          };
          if (mode === 'quickbook')
            logAmplitudeEvent(
              'Book_Appointment_Created',
              Object.assign(eventProps, { bookingMode: 'quickbook' }),
              true
            );
          else logAmplitudeEvent('Book_Appointment_Created', eventProps, true);
        }}
      >
        {(createBooking, { data, loading }) => {
          const { createBooking: bookingRes } = data || {};
          // console.log('data got', data);
          // if(error) returndisplayError(JSON.stringify(error));
          return !data ? (
            <fieldset disabled={loading}>
              <Row className="form-row">
                {loading && <Veil />}
                <Col xs={4} className="text-right" style={styles.formLabel}>
                  Appointment Time<div style={{ color: 'red' }}>*</div>:
                </Col>
                <Col xs={8}>
                  <input readOnly value={slotTime} />
                </Col>
              </Row>
              <Row className="form-row">
                <Col xs={4} className="text-right" style={styles.formLabel}>
                  Search or Add by Mobile<div style={{ color: 'red' }}>*</div>:
                </Col>
                <Col xs={8}>
                  <PatientSearchByPhone
                    hospitalId={hospitalId}
                    onChange={this.handleUserData}
                    onInputChange={this.handlePhoneUpdate}
                  />
                </Col>
              </Row>
              {isPatientSelected && (
                <ApptBookingLongForm
                  isNewPatient={isNewPatient}
                  hasMPI={hasMPI}
                  bookingData={booking}
                  onChange={this.updateBooking}
                  onSubmit={() => {
                    showConfirmation(
                      'Booking Confirmation',
                      this.getConfirmationMessage(),
                      () => this.submitBooking(createBooking)
                    );
                  }}
                  hospitalId={hospitalId}
                  selectedSlot={selectedSlot}
                  procedureName={procedureName}
                  doctorId={doctorId}
                  isScanBooking={isScanBooking}
                />
              )}
            </fieldset>
          ) : (
            <BookingInfo bookingId={bookingRes.id} />
          );
        }}
      </Mutation>
    );
  }
}

BookApptForm.propTypes = {
  selectedSlot: PropTypes.object,
  procedureName: PropTypes.string,
  mode: PropTypes.string,
  hospitalId: PropTypes.number,
  doctorId: PropTypes.number,
  doctorName: PropTypes.string,
  hospitalName: PropTypes.string,
  scanName: PropTypes.string,
  isScanBooking: PropTypes.bool
};
